'use client';

import { Button } from '@/app/_components/core/button';
import { SectionIntro } from '@/app/_components/core/section-intro';
import { SmartLink } from '@/app/_components/core/smart-link';

export interface ErrorSectionProps {
  tagline?: string;
  title?: string;
  description?: string;
  resetButtonLabel?: string;
  defaultButtonLabel?: string;
  homeHref?: string;
  showResetButton?: boolean;
  onReset?: () => void;
  error?: Error & { digest?: string };
}

export function ErrorSection({
  tagline = 'Error',
  title = 'Page not found',
  description = 'Sorry, an error occurred. Please check the URL or navigate back home.',
  showResetButton = false,
  resetButtonLabel = 'Try again',
  defaultButtonLabel = 'Go to homepage',
  homeHref = '/',
  onReset,
  error,
}: ErrorSectionProps) {
  return (
    <section
      data-component="error-section"
      className="container flex min-h-svh items-center justify-center bg-background pb-safe-bottom"
      aria-labelledby="error-title"
    >
      <div className="relative z-10 container mx-auto flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
        <div className="m-auto flex max-w-xl flex-1 flex-col items-center gap-6 text-center lg:gap-8">
          {/* Section Title */}
          <SectionIntro
            intro={{
              subline: tagline,
              headline: title,
              headlineType: 'h1',
              introtext: `<p>${description}</p>`,
              introAlignment: 'center',
            }}
            className="mb-0"
          />

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            {showResetButton && (
              <Button onClick={onReset} className="cursor-pointer" data-track-id="error-section-reset">
                {resetButtonLabel}
              </Button>
            )}
            <Button variant={showResetButton ? 'outline' : 'default'} asChild>
              <SmartLink href={homeHref} data-track-id="error-section-home">{defaultButtonLabel}</SmartLink>
            </Button>
          </div>

          {/* Development Error Details */}
          {process.env.NODE_ENV === 'development' && error && (
            <pre className="mt-2 overflow-auto rounded-sm border bg-background p-4 text-xs">
              Only visible for DEV:
              {error.message}
              {error.digest && `\nDigest: ${error.digest}`}
            </pre>
          )}
        </div>
      </div>
    </section>
  );
}
